import { useEffect, useRef, useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { handoffStarted, markHandoffStarted, missingHandoffFields } from '@/lib/agentHandoff';

import { TwinneeLogo } from '@/components/TwinneeLogo';
/**
 * Shown on a flow page opened by the create agent. The page passes its required fields
 * (`{ id, label, hint?, valid, render() }`) after applying the agent's answers (`ready`);
 * only the ones still unanswered are asked, then `onStart` (the page's own start
 * function) runs. With `handoff.autoStart` and nothing missing it starts right away.
 */
export function AgentHandoffGate({
  handoff,
  ready,
  flowLabel = 'this flow',
  fields = [],
  onStart,
  busy = false,
  startLabel = 'Start generation',
}) {
  const [open, setOpen] = useState(false);
  const [askIds, setAskIds] = useState([]);
  const decidedRef = useRef(false);
  const planId = handoff?.planId || '';

  const start = () => {
    if (!planId) return;
    markHandoffStarted(planId);
    setOpen(false);
    onStart?.();
  };

  useEffect(() => {
    if (!planId || !ready || decidedRef.current) return;
    decidedRef.current = true;
    if (handoffStarted(planId)) return;
    const missing = missingHandoffFields(fields);
    if (handoff.autoStart && !missing.length) {
      start();
      return;
    }
    // Freeze the list so a field doesn't vanish from the dialog as soon as it becomes valid.
    setAskIds(missing.map((f) => f.id));
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, ready]);

  if (!planId) return null;
  const asked = fields.filter((f) => askIds.includes(f.id));
  const canStart = !busy && missingHandoffFields(fields).length === 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="agent-handoff-gate">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TwinneeLogo className="w-4 h-4 text-[#2DD4BF]" />
            {asked.length ? 'Almost ready to generate' : 'Ready to generate'}
          </DialogTitle>
          <DialogDescription>
            {asked.length
              ? `The create agent already has your other answers. ${flowLabel} still needs ${
                  asked.length === 1 ? 'one more detail' : `${asked.length} more details`
                }:`
              : `Everything ${flowLabel} needs came from the create agent.`}
          </DialogDescription>
        </DialogHeader>
        {asked.length ? (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            {asked.map((f) => (
              <div key={f.id} className="space-y-1.5" data-testid={`agent-handoff-field-${f.id}`}>
                <p className="text-sm font-semibold">{f.label}</p>
                {f.hint ? <p className="text-xs text-muted-foreground">{f.hint}</p> : null}
                {f.render()}
              </div>
            ))}
          </div>
        ) : null}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Review full form
          </Button>
          <Button type="button" onClick={start} disabled={!canStart} data-testid="agent-handoff-start">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
            {startLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AgentHandoffGate;
